import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { finalize } from 'rxjs';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

interface OnlineUserItem {
  id?: string;
  accountId?: string;
  tokenId?: string;
  name?: string;
  countryCode?: string;
  mobile?: string;
  deviceName?: string;
  deviceType?: string;
  osType?: string;
  channel?: string;
  loginType?: string | number;
  thirdChannel?: string;
  uniqueId?: string;
  bundleId?: string;
  ip?: string;
  address?: string;
  updateTime?: number;
}

interface SocketioConnectionAuditItem {
  id?: string;
  sid?: string;
  key?: string;
  accountId?: string;
  name?: string;
  uniqueId?: string;
  channel?: string;
  osType?: string;
  bundleId?: string;
  deviceName?: string;
  deviceType?: string;
  deviceIp?: string;
  connectTime?: number;
  disconnectTime?: number;
  durationMs?: number;
  connected?: boolean;
  disconnectReason?: string;
  disconnectSource?: string;
  disconnectDetail?: string;
  forcedOffline?: boolean;
  squeezedOut?: boolean;
  updateTime?: number;
}

interface SocketioConnectionAuditEventItem {
  sequence?: number;
  messageId?: string;
  eventName?: string;
  eventCode?: string;
  event?: unknown;
  sid?: string;
  socketioSuccess?: boolean;
  offlineMessageStored?: boolean;
  pushSuccess?: boolean;
  createTime?: number;
  [key: string]: unknown;
}

interface SocketioAuditSocketTargetItem {
  sequence?: number;
  nodeId?: string;
  success?: boolean;
  ackEnabled?: boolean;
  updateTime?: number;
  failureReason?: string;
  exceptionMessage?: string;
  sessionId?: string;
}

interface SocketioAuditPushTargetItem {
  appToken?: string;
  targetId?: string;
  osType?: string;
  success?: boolean;
  failureReason?: string;
  exceptionMessage?: string;
  updateTime?: number;
}

interface SocketioAuditDetailItem extends SocketioConnectionAuditEventItem {
  key?: string;
  accountId?: string;
  socketioSessionId?: string;
  socketioSequence?: number;
  socketioTargets?: SocketioAuditSocketTargetItem[];
  pushTargets?: SocketioAuditPushTargetItem[];
  messageRaw?: string;
  traceId?: string;
  updateTime?: number;
}

@Component({
  selector: 'app-online-users',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    NzButtonModule,
    NzCardModule,
    NzIconModule,
    NzInputModule,
    NzModalModule,
    NzPaginationModule,
    NzSelectModule,
    NzSpinModule,
    NzTableModule,
    I18nPipe,
    TestIdDirective
  ],
  templateUrl: './online-users.component.html',
  styleUrls: ['./online-users.component.less']
})
export class OnlineUsersComponent implements OnInit {
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);

  loading = false;
  pageSize = 20;
  pageIndex = 1;
  total = 0;
  readonly tableScrollX = '1468px';
  onlineUsers: OnlineUserItem[] = [];
  searchKey = '';
  refreshTime = Date.now();
  auditModalVisible = false;
  auditModalLoading = false;
  auditAccountId = '';
  auditAccountName = '';
  auditContext = {
    mobileDisplay: '',
    osType: '',
    channel: '',
    loginMethod: '',
    loginTime: undefined as number | undefined
  };
  auditPageIndex = 1;
  auditPageSize = 10;
  auditTotal = 0;
  auditLogs: SocketioConnectionAuditItem[] = [];
  auditFilters = {
    sid: '',
    key: '',
    uniqueId: '',
    osType: '',
    channel: '',
    disconnectReason: '',
    connected: ''
  };
  eventModalVisible = false;
  eventModalLoading = false;
  eventLogs: SocketioConnectionAuditEventItem[] = [];
  eventModalTitle = '';
  eventTargetSid = '';
  eventTargetKey = '';
  eventTargetConnectTime?: number;
  eventTargetDisconnectTime?: number;
  eventDetailVisible = false;
  eventDetailLoading = false;
  selectedEvent: SocketioConnectionAuditEventItem | null = null;
  selectedEventDetail: SocketioAuditDetailItem | null = null;
  tokenModalVisible = false;
  selectedToken = '';

  readonly columns = [
    { title: this.i18n.fanyi('socketio.audit.online-sidebar.login-time'), key: 'updateTime', width: '176px' },
    { title: this.i18n.fanyi('account.list.account-id'), key: 'accountId', width: '260px' },
    { title: this.i18n.fanyi('socketio.audit.online-sidebar.name'), key: 'name', width: '140px' },
    { title: this.i18n.fanyi('account.list.phone-number'), key: 'mobileDisplay', width: '150px' },
    { title: this.i18n.fanyi('account.list.account-source'), key: 'channel', width: '90px' },
    { title: this.i18n.fanyi('socketio.audit.online-sidebar.os-type'), key: 'osType', width: '76px' },
    { title: this.i18n.fanyi('socketio.audit.online-sidebar.login-method'), key: 'loginMethod', width: '220px' },
    { title: this.i18n.fanyi('log.token-id'), key: 'tokenId', width: '200px' }
  ];

  get onlineUserTotal(): number {
    return this.total;
  }

  get auditModalTitle(): string {
    return this.auditAccountName || this.auditAccountId || this.i18n.fanyi('socketio.audit.drawer.default-title');
  }

  get connectedAuditCount(): number {
    return this.auditLogs.filter(item => item.connected).length;
  }

  get hasAuditFilters(): boolean {
    return Object.values(this.auditFilters).some(value => String(value || '').trim() !== '');
  }

  get auditAllEventKey(): string {
    return String(this.auditFilters.key || this.auditLogs.find(item => item.key)?.key || '').trim();
  }

  constructor(
    private readonly http: HttpService,
    private readonly message: NzMessageService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  loadData(reset: boolean = false): void {
    if (reset) {
      this.pageIndex = 1;
    }

    this.loading = true;
    const params = {
      key: this.searchKey?.trim() || undefined,
      pageIndex: this.pageIndex - 1,
      pageSize: this.pageSize,
      direction: 'desc',
      orderBy: 'updateTime',
      refreshTime: this.refreshTime
    };

    this.http
      .post(ConstantUtil.ACCOUNT_ONLINE_LIST, params)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (res: any) => {
          if (!res?.success) {
            this.message.error(res?.msg ?? this.i18n.fanyi('common.load-failed'));
            return;
          }

          const items = Array.isArray(res.data?.items) ? res.data.items : [];
          this.onlineUsers = items.map((item: OnlineUserItem) => ({
            ...item,
            accountId: item.accountId || item.id || '--'
          }));
          this.total = Number(res.data?.total ?? res.data?.totalCount ?? res.data?.count ?? this.onlineUsers.length ?? 0);
          this.cdr.detectChanges();
        },
        error: () => this.message.error(this.i18n.fanyi('common.load-failed'))
      });
  }

  search(): void {
    this.refreshTime = Date.now();
    this.loadData(true);
  }

  reset(): void {
    this.searchKey = '';
    this.refreshTime = Date.now();
    this.loadData(true);
  }

  refresh(): void {
    this.refreshTime = Date.now();
    this.loadData();
  }

  nzPageIndexChange(index: number): void {
    this.pageIndex = index;
    this.loadData();
  }

  nzPageSizeChange(size: number): void {
    this.pageSize = size;
    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.loadData();
  }

  viewAccount(item: OnlineUserItem): void {
    const accountId = item.accountId;
    if (!accountId || accountId === '--') {
      this.message.error(this.i18n.fanyi('account.list.account-id-required'));
      return;
    }
    this.auditAccountId = accountId;
    this.auditAccountName = item.name || '';
    this.auditContext = {
      mobileDisplay: this.getMobileDisplay(item),
      osType: String(item.osType || '').trim(),
      channel: String(item.channel || '').trim(),
      loginMethod: this.getLoginMethodDisplay(item),
      loginTime: item.updateTime
    };
    this.auditPageIndex = 1;
    this.auditFilters = {
      sid: '',
      key: '',
      uniqueId: String(item.uniqueId || '').trim(),
      osType: '',
      channel: '',
      disconnectReason: '',
      connected: ''
    };
    this.auditModalVisible = true;
    this.loadAuditLogs();
  }

  closeAuditModal(): void {
    this.auditModalVisible = false;
    this.auditLogs = [];
    this.auditTotal = 0;
    this.auditAccountId = '';
    this.auditAccountName = '';
    this.auditContext = {
      mobileDisplay: '',
      osType: '',
      channel: '',
      loginMethod: '',
      loginTime: undefined
    };
    this.auditFilters = {
      sid: '',
      key: '',
      uniqueId: '',
      osType: '',
      channel: '',
      disconnectReason: '',
      connected: ''
    };
    this.closeEventModal();
  }

  onAuditPageIndexChange(index: number): void {
    this.auditPageIndex = index;
    this.loadAuditLogs();
  }

  onAuditPageSizeChange(size: number): void {
    this.auditPageSize = size;
    this.auditPageIndex = 1;
    this.loadAuditLogs();
  }

  searchAuditLogs(): void {
    this.auditPageIndex = 1;
    this.loadAuditLogs();
  }

  resetAuditFilters(): void {
    this.auditFilters = {
      sid: '',
      key: '',
      uniqueId: '',
      osType: '',
      channel: '',
      disconnectReason: '',
      connected: ''
    };
    this.auditPageIndex = 1;
    this.loadAuditLogs();
  }

  getMobileDisplay(item: OnlineUserItem): string {
    const normalizedCode = item.countryCode
      ? String(item.countryCode).startsWith('+')
        ? String(item.countryCode)
        : `+${item.countryCode}`
      : '';
    const code = normalizedCode ? `${normalizedCode} ` : '';
    return item.mobile ? `${code}${item.mobile}`.trim() : '--';
  }

  getLoginMethodDisplay(item: OnlineUserItem): string {
    const loginType = String(item.loginType ?? '').trim();
    const thirdChannel = String(item.thirdChannel ?? '').trim();
    const loginTypeLabelMap: Record<string, string> = {
      '2': 'socketio.audit.online-sidebar.login-type.third-party',
      '3': 'socketio.audit.online-sidebar.login-type.otp',
      '5': 'socketio.audit.online-sidebar.login-type.once-token',
      '6': 'socketio.audit.online-sidebar.login-type.device',
      '7': 'socketio.audit.online-sidebar.login-type.qrcode',
      '10': 'socketio.audit.online-sidebar.login-type.auto',
      '11': 'socketio.audit.online-sidebar.login-type.anonymous'
    };
    const labelKey = loginTypeLabelMap[loginType];

    if (!labelKey) return '--';

    const label = this.i18n.fanyi(labelKey);
    if (loginType !== '2' || !thirdChannel) return label;
    return this.i18n.fanyi('socketio.audit.online-sidebar.login-type.third-party-with-channel', {
      label,
      channel: thirdChannel
    });
  }

  getLocationDisplay(item: OnlineUserItem): string {
    return item.address || item.ip || '--';
  }

  getTokenPreview(value: string | undefined): string {
    const token = String(value || '').trim();
    if (!token) return '--';
    if (token.length <= 10) return token;
    return `${token.slice(0, 4)}...${token.slice(-4)}`;
  }

  getOsTypeDisplay(item: OnlineUserItem): string {
    return item.osType || '--';
  }

  getAuditDeviceDisplay(item: SocketioConnectionAuditItem): string {
    return item.deviceName || item.deviceType || item.osType || item.channel || '--';
  }

  getAuditStatusLabel(item: SocketioConnectionAuditItem): string {
    return item.connected ? this.i18n.fanyi('socketio.audit.drawer.connected') : this.i18n.fanyi('socketio.audit.drawer.disconnected');
  }

  getAuditChannelDisplay(item: SocketioConnectionAuditItem): string {
    return item.channel || item.bundleId || item.osType || '--';
  }

  getAuditDurationDisplay(durationMs: number | null | undefined): string {
    const milliseconds = Number(durationMs);
    if (!Number.isFinite(milliseconds) || milliseconds < 0) return '--';

    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const padTime = (value: number): string => String(value).padStart(2, '0');

    return `${padTime(hours)}:${padTime(minutes)}:${padTime(seconds)}`;
  }

  viewAuditEvents(item: SocketioConnectionAuditItem): void {
    const sid = item.sid || '';
    const key = item.key || '';
    if (!sid) {
      this.message.error(this.i18n.fanyi('socketio.audit.events.empty'));
      return;
    }

    this.eventTargetSid = sid;
    this.eventTargetKey = key;
    this.eventTargetConnectTime = item.connectTime;
    this.eventTargetDisconnectTime = item.disconnectTime;
    this.eventModalTitle = item.sid || item.key || this.i18n.fanyi('socketio.audit.events.title');
    this.eventModalVisible = true;
    this.loadAuditEvents();
  }

  viewAllAuditEvents(item?: SocketioConnectionAuditItem): void {
    const key = String(item?.key || this.auditAllEventKey || '').trim();
    if (!key) {
      this.message.error(this.i18n.fanyi('socketio.audit.events.empty'));
      return;
    }

    this.eventTargetSid = '';
    this.eventTargetKey = key;
    this.eventTargetConnectTime = undefined;
    this.eventTargetDisconnectTime = undefined;
    this.eventModalTitle = this.i18n.fanyi('socketio.audit.events.all');
    this.eventModalVisible = true;
    this.loadAuditEvents();
  }

  closeEventModal(): void {
    this.eventModalVisible = false;
    this.eventModalLoading = false;
    this.eventModalTitle = '';
    this.eventTargetSid = '';
    this.eventTargetKey = '';
    this.eventTargetConnectTime = undefined;
    this.eventTargetDisconnectTime = undefined;
    this.eventLogs = [];
    this.closeEventDetail();
  }

  getEventDeliveryLabel(item: SocketioConnectionAuditEventItem): string {
    const status = [];
    if (item.socketioSuccess) status.push(this.i18n.fanyi('socketio.audit.events.socketio-success'));
    if (item.offlineMessageStored) status.push(this.i18n.fanyi('socketio.audit.events.offline-stored'));
    if (item.pushSuccess) status.push(this.i18n.fanyi('socketio.audit.events.push-success'));
    return status.length ? status.join(' / ') : '--';
  }

  getEventPayloadDisplay(value: unknown): string {
    return this.formatDebugValue(value);
  }

  getEventRawMessageDisplay(item: SocketioAuditDetailItem | SocketioConnectionAuditEventItem | null): string {
    return this.formatDebugValue(this.getEventRawMessageValue(item));
  }

  getEventRecordDisplay(item: SocketioAuditDetailItem | SocketioConnectionAuditEventItem | null): string {
    return this.formatDebugValue(item);
  }

  getEventSocketioStatusDisplay(item: SocketioAuditDetailItem | SocketioConnectionAuditEventItem | null): string {
    return this.getBooleanStatusLabel(
      item?.socketioSuccess,
      this.i18n.fanyi('socketio.audit.delivery.success'),
      this.i18n.fanyi('socketio.audit.delivery.failed'),
      '--'
    );
  }

  getEventOfflineMessageStatusDisplay(item: SocketioAuditDetailItem | SocketioConnectionAuditEventItem | null): string {
    return this.getBooleanStatusLabel(
      item?.offlineMessageStored,
      this.i18n.fanyi('socketio.audit.delivery.offline-stored'),
      this.i18n.fanyi('socketio.audit.delivery.offline-not-stored'),
      '--'
    );
  }

  getEventPushStatusDisplay(item: SocketioAuditDetailItem | SocketioConnectionAuditEventItem | null): string {
    return this.getBooleanStatusLabel(
      item?.pushSuccess,
      this.i18n.fanyi('socketio.audit.delivery.success'),
      this.i18n.fanyi('socketio.audit.delivery.failed'),
      this.i18n.fanyi('socketio.audit.delivery.push-not-sent')
    );
  }

  getEventStatusDisplay(item: SocketioAuditDetailItem | SocketioConnectionAuditEventItem | null): string {
    const parsedMessageRaw = this.getParsedMessageRaw(item);
    const candidates = [
      this.getLatestSocketTarget(item)?.success === true
        ? this.i18n.fanyi('log.success-status')
        : this.getLatestSocketTarget(item)?.success === false
          ? this.i18n.fanyi('log.failed-status')
          : undefined,
      item?.['status'],
      this.getNestedValue(item, ['content', 'status']),
      this.getNestedValue(item, ['event', 'status']),
      this.getNestedValue(item, ['payload', 'status']),
      this.getNestedValue(parsedMessageRaw, ['status']),
      this.getNestedValue(parsedMessageRaw, ['content', 'status'])
    ];
    return this.getFirstMeaningfulValue(candidates);
  }

  getEventNodeDisplay(item: SocketioAuditDetailItem | SocketioConnectionAuditEventItem | null): string {
    const parsedMessageRaw = this.getParsedMessageRaw(item);
    const candidates = [
      this.getLatestSocketTarget(item)?.nodeId,
      item?.['node'],
      item?.['nodeName'],
      this.getNestedValue(item, ['content', 'node']),
      this.getNestedValue(item, ['content', 'nodeName']),
      this.getNestedValue(item, ['event', 'node']),
      this.getNestedValue(item, ['event', 'nodeName']),
      this.getNestedValue(item, ['payload', 'node']),
      this.getNestedValue(item, ['payload', 'nodeName']),
      this.getNestedValue(parsedMessageRaw, ['node']),
      this.getNestedValue(parsedMessageRaw, ['nodeName']),
      this.getNestedValue(parsedMessageRaw, ['content', 'node']),
      this.getNestedValue(parsedMessageRaw, ['content', 'nodeName'])
    ];
    return this.getFirstMeaningfulValue(candidates);
  }

  getEventTraceIdDisplay(item: SocketioAuditDetailItem | null): string {
    return item?.traceId || '--';
  }

  viewEventDetail(item: SocketioConnectionAuditEventItem): void {
    this.selectedEvent = item;
    this.eventDetailVisible = true;
    this.eventDetailLoading = true;
    this.selectedEventDetail = null;
    this.loadEventDetail(item);
  }

  openTokenModal(token: string | undefined): void {
    this.selectedToken = String(token || '').trim();
    this.tokenModalVisible = true;
  }

  closeTokenModal(): void {
    this.tokenModalVisible = false;
    this.selectedToken = '';
  }

  async copyToken(token: string | undefined, event?: Event): Promise<void> {
    event?.stopPropagation();
    const value = String(token || '').trim();
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      this.message.success(this.i18n.fanyi('common.copy-success'));
    } catch {
      this.message.error(this.i18n.fanyi('common.copy-failed'));
    }
  }

  closeEventDetail(): void {
    this.eventDetailVisible = false;
    this.eventDetailLoading = false;
    this.selectedEvent = null;
    this.selectedEventDetail = null;
  }

  private loadAuditLogs(): void {
    if (!this.auditAccountId) return;

    this.auditModalLoading = true;
    const params = {
      accountId: this.auditAccountId,
      sid: this.auditFilters.sid?.trim() || undefined,
      key: this.auditFilters.key?.trim() || undefined,
      uniqueId: this.auditFilters.uniqueId?.trim() || undefined,
      osType: this.auditContext.osType || undefined,
      channel: this.auditContext.channel || undefined,
      disconnectReason: this.auditFilters.disconnectReason?.trim() || undefined,
      connected: this.auditFilters.connected === '' ? undefined : this.auditFilters.connected === 'true',
      pageIndex: this.auditPageIndex - 1,
      pageSize: this.auditPageSize,
      direction: 'desc',
      orderBy: 'updateTime',
      refreshTime: Date.now()
    };

    this.http
      .post(ConstantUtil.NOTICE_AUDIT_SOCKETIO_CONNECTION_LIST, params)
      .pipe(
        finalize(() => {
          this.auditModalLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (res: any) => {
          if (!res?.success) {
            this.message.error(res?.msg ?? this.i18n.fanyi('common.load-failed'));
            return;
          }

          this.auditLogs = Array.isArray(res.data?.items) ? res.data.items : [];
          this.auditTotal = Number(res.data?.total ?? res.data?.totalCount ?? res.data?.count ?? this.auditLogs.length ?? 0);
          this.cdr.detectChanges();
        },
        error: () => this.message.error(this.i18n.fanyi('common.load-failed'))
      });
  }

  private loadAuditEvents(): void {
    this.eventModalLoading = true;
    this.fetchAuditEventsBySidOrKey();
  }

  private fetchAuditEventsBySidOrKey(): void {
    const sid = this.eventTargetSid || undefined;
    const key = this.eventTargetKey || undefined;
    const primaryQuery = sid ? { sid } : { key };

    this.http.get(ConstantUtil.NOTICE_AUDIT_SOCKETIO_CONNECTION_EVENTS, primaryQuery, { aileMode: 'develop' }).subscribe({
      next: (res: any) => {
        if (!res?.success) {
          this.finishEventLoadingWithError(res?.msg);
          return;
        }

        this.finishEventLoading(Array.isArray(res.data) ? res.data : []);
      },
      error: () => this.finishEventLoadingWithError()
    });
  }

  private finishEventLoading(items: SocketioConnectionAuditEventItem[]): void {
    this.eventLogs = this.filterEventsForCurrentConnection(items).sort(
      (a: SocketioConnectionAuditEventItem, b: SocketioConnectionAuditEventItem) => {
        const timeDiff = Number(b.createTime || 0) - Number(a.createTime || 0);
        if (timeDiff !== 0) return timeDiff;
        return Number(b.sequence || 0) - Number(a.sequence || 0);
      }
    );
    this.eventModalLoading = false;
    this.cdr.detectChanges();
  }

  private filterEventsForCurrentConnection(items: SocketioConnectionAuditEventItem[]): SocketioConnectionAuditEventItem[] {
    const sid = String(this.eventTargetSid || '').trim();
    let filtered = [...items];

    if (sid) {
      const sidFiltered = filtered.filter(item => this.getEventSid(item) === sid);
      if (sidFiltered.length > 0) {
        filtered = sidFiltered;
      }
    }

    const connectTime = Number(this.eventTargetConnectTime || 0);
    const disconnectTime = Number(this.eventTargetDisconnectTime || 0);
    if (connectTime && disconnectTime) {
      const timeFiltered = filtered.filter(item => {
        const createTime = Number(item.createTime || 0);
        return createTime >= connectTime && createTime <= disconnectTime;
      });
      if (timeFiltered.length > 0) {
        filtered = timeFiltered;
      }
    }

    return filtered;
  }

  private getEventSid(item: SocketioConnectionAuditEventItem): string {
    return String(item.sid || item['socketioSessionId'] || '').trim();
  }

  private finishEventLoadingWithError(message?: string): void {
    this.eventModalLoading = false;
    this.cdr.detectChanges();
    this.message.error(message ?? this.i18n.fanyi('common.load-failed'));
  }

  private formatDebugValue(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '--';
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return '--';

      try {
        const parsed = JSON.parse(trimmed);
        return JSON.stringify(parsed, null, 2);
      } catch {
        return value;
      }
    }

    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

  private getEventRawMessageValue(item: SocketioAuditDetailItem | SocketioConnectionAuditEventItem | null): unknown {
    if (!item) return '--';

    if ('messageRaw' in item && item.messageRaw) {
      return item.messageRaw;
    }

    const directRawCandidates = [item['rawMessage'], item['originalMessage'], item['originMessage'], item['message'], item['payload']];
    for (const candidate of directRawCandidates) {
      if (candidate !== null && candidate !== undefined && candidate !== '') {
        return candidate;
      }
    }

    const rawKeys = [
      'code',
      'name',
      'event',
      'content',
      'tenantId',
      'createTime',
      'sequence',
      'status',
      'baseName',
      'sourceType',
      'parentCode',
      'id',
      'accountId',
      'serviceNumberId',
      'employeeId',
      'tenantAdminId'
    ];
    const rawObject = rawKeys.reduce<Record<string, unknown>>((result, key) => {
      const value = item[key];
      if (value !== null && value !== undefined && value !== '') {
        result[key] = value;
      }
      return result;
    }, {});

    if (Object.keys(rawObject).length > 0) {
      return rawObject;
    }

    return item;
  }

  private getParsedMessageRaw(item: SocketioAuditDetailItem | SocketioConnectionAuditEventItem | null): unknown {
    const raw = item && 'messageRaw' in item ? item.messageRaw : undefined;
    if (!raw || typeof raw !== 'string') return undefined;

    try {
      return JSON.parse(raw);
    } catch {
      return undefined;
    }
  }

  private getLatestSocketTarget(
    item: SocketioAuditDetailItem | SocketioConnectionAuditEventItem | null
  ): SocketioAuditSocketTargetItem | null {
    if (!item || !('socketioTargets' in item) || !Array.isArray(item.socketioTargets) || !item.socketioTargets.length) {
      return null;
    }
    return item.socketioTargets[item.socketioTargets.length - 1] || null;
  }

  private loadEventDetail(item: SocketioConnectionAuditEventItem): void {
    const messageId = String(item.messageId || '').trim();
    const key = String(this.eventTargetKey || '').trim();

    if (!messageId || !key) {
      this.eventDetailLoading = false;
      this.cdr.detectChanges();
      return;
    }

    const params = {
      pageIndex: 0,
      pageSize: 20,
      messageId,
      key,
      direction: 'desc',
      orderBy: 'updateTime'
    };

    this.http.post(ConstantUtil.NOTICE_AUDIT_SOCKETIO_LIST, params, { aileMode: 'develop' }).subscribe({
      next: (res: any) => {
        if (!res?.success) {
          this.eventDetailLoading = false;
          this.message.error(res?.msg ?? this.i18n.fanyi('common.load-failed'));
          this.cdr.detectChanges();
          return;
        }

        const items = Array.isArray(res.data?.items) ? res.data.items : [];
        this.selectedEventDetail = this.selectMatchingEventDetail(items, item);
        this.eventDetailLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.eventDetailLoading = false;
        this.message.error(this.i18n.fanyi('common.load-failed'));
        this.cdr.detectChanges();
      }
    });
  }

  private selectMatchingEventDetail(
    items: SocketioAuditDetailItem[],
    eventItem: SocketioConnectionAuditEventItem
  ): SocketioAuditDetailItem | null {
    const sid = String(eventItem.sid || '').trim();

    return (
      items.find(detail => {
        if (sid && detail.socketioSessionId === sid) {
          return true;
        }
        return (
          Array.isArray(detail.socketioTargets) && detail.socketioTargets.some(target => String(target.sessionId || '').trim() === sid)
        );
      }) ||
      items[0] ||
      null
    );
  }

  private getNestedValue(source: unknown, path: string[]): unknown {
    if (!source || typeof source !== 'object') return undefined;

    let current: unknown = source;
    for (const key of path) {
      if (!current || typeof current !== 'object' || !(key in current)) {
        return undefined;
      }
      current = (current as Record<string, unknown>)[key];
    }
    return current;
  }

  private getFirstMeaningfulValue(values: unknown[]): string {
    for (const value of values) {
      if (value === null || value === undefined) continue;
      const text = String(value).trim();
      if (text) return text;
    }
    return '--';
  }

  private getBooleanStatusLabel(value: unknown, trueLabel: string, falseLabel: string, unsetLabel: string): string {
    if (value === true) return trueLabel;
    if (value === false) return falseLabel;
    return unsetLabel;
  }
}
